import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ApiResponse, ApiError } from '../types';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';

// Create axios instance with base configuration
const createApiClient = (): AxiosInstance => {
  // Use relative URL for same-origin requests (works with Vite proxy in dev and same domain in prod)
  const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';
  console.log('API Client Base URL:', baseURL, 'ENV:', import.meta.env);
  
  const client = axios.create({
    baseURL,
    timeout: 30000, // 30 seconds
    withCredentials: true, // Enable sending cookies with requests
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - cookies are automatically sent by the browser
  client.interceptors.request.use(
    (config) => {
      // Debug logging for upload requests
      if (config.url?.includes('/upload/initiate')) {
        console.log('API Client - Request config:', {
          url: config.url,
          method: config.method,
          data: config.data,
          headers: config.headers
        });
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      return response;
    },
    (error: AxiosError<ApiResponse>) => {
      const { response } = error;
      
      if (response?.status === 401) {
        // Unauthorized - just clear auth state, let the App component handle the flow
        try {
          useAuthStore.getState().clearAuth();
        } catch {
          // Ignore errors when clearing auth
        }
        return Promise.reject(error);
      }

      if (response?.status === 403) {
        toast.error('Access denied. You do not have permission to perform this action.');
      }

      if (response && response.status >= 500) {
        toast.error('Server error. Please try again later.');
      }

      // Handle network errors
      if (!response) {
        toast.error('Network error. Please check your connection and try again.');
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createApiClient();

// Helper function to handle API responses
export const handleApiResponse = <T>(response: AxiosResponse<ApiResponse<T>>): ApiResponse<T> => {
  return response.data;
};

// Helper function to handle API errors
export const handleApiError = (error: AxiosError<ApiResponse>): ApiError => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  return {
    message: error.message || 'An unexpected error occurred',
    statusCode: error.response?.status || 500,
    timestamp: new Date().toISOString(),
  };
};